/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/AgentCards.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useState } from 'react';
import { Check, X, Pencil, CircleCheck, CircleAlert, Info, TriangleAlert, LoaderCircle, Play, ArrowRight, MessageCircleQuestion } from 'lucide-react';
import { I, Badge, Confidence, Spinner } from './ui';
import { api } from '../lib/api';
import { amt, chf, date } from '../lib/format';
import { navigate } from '../lib/router';
import { useApp } from '../lib/store';
import { useAccounts } from '../lib/accounts';

export const AGENT_LABEL: Record<string, string> = { intake: 'Eingang', bookkeeper: 'Buchhalter', auditor: 'Auditor', policy: 'Policy', reconciler: 'Bank', accountant: 'Kontor', finma: 'Enforcement' };

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, [string, 'pos' | 'warn' | 'neg' | 'accent' | undefined]> = {
    auto_posted: ['Automatisch verbucht', 'pos'], approved: ['Freigegeben', 'pos'], pending: ['Zur Prüfung', 'warn'], rejected: ['Abgelehnt', 'neg'],
  };
  const [label, kind] = map[status] ?? [status, undefined];
  return <Badge kind={kind} dot>{label}</Badge>;
}

export function Steps({ steps }: { steps: { id: string; agent: string; label: string; detail?: string; state: string }[] }) {
  if (!steps.length) return null;
  return (
    <div className="timeline" data-tour="timeline">
      {steps.map((s) => (
        <div className="step" key={s.id} data-state={s.state}>
          <span className="state">{s.state === 'running' ? <I icon={LoaderCircle} sm spin /> : s.state === 'error' ? <I icon={CircleAlert} sm /> : <I icon={CircleCheck} sm />}</span>
          <span className="who">{AGENT_LABEL[s.agent] ?? s.agent}</span>
          <span className="what">{s.label}{s.detail && <span className="detail">{s.detail}</span>}</span>
        </div>
      ))}
    </div>
  );
}

function Lines({ tid, lines }: { tid: string; lines: { account: string; side: string; amount: number; vatCode?: string | null }[] }) {
  const accounts = useAccounts(tid);
  return (
    <table className="lines" data-copy-surface>
      <tbody>
        {lines.map((l, i) => (
          <tr key={i}>
            <td style={{ width: '3.5rem' }} className="muted">{l.side === 'D' ? 'Soll' : 'Haben'}</td>
            <td><span className="mono">{l.account}</span> <span className="acct">{accounts[l.account] ?? ''}</span>{l.vatCode && <> <Badge>{l.vatCode}</Badge></>}</td>
            <td className="r num">{amt(l.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Findings({ findings }: { findings: { severity: string; message: string }[] }) {
  if (!findings?.length) return null;
  return (
    <div className="findings">
      {findings.map((f, i) => (
        <div className="finding" data-sev={f.severity} key={i}><I icon={f.severity === 'error' ? CircleAlert : f.severity === 'warning' ? TriangleAlert : Info} sm /><span>{f.message}</span></div>
      ))}
    </div>
  );
}

export function ProposalCard({ tid, card, onDecided }: { tid: string; card: any; onDecided?: () => void }) {
  const { toast, bump } = useApp();
  const [status, setStatus] = useState<string>(card.status);
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (kind: 'approve' | 'reject') => {
    setBusy(kind);
    try {
      await api.post(`/api/t/${tid}/proposals/${card.proposalId}/${kind}`);
      setStatus(kind === 'approve' ? 'approved' : 'rejected');
      toast(kind === 'approve' ? 'Gebucht. Der Agent merkt sich das Muster.' : 'Abgelehnt — Beleg wartet auf Klärung.');
      bump(); onDecided?.();
    } catch (e: any) { toast(e.message, 'error'); }
    setBusy(null);
  };
  return (
    <div className="acard">
      <div className="acard-head">
        <span className="title">{card.description}</span>
        <StatusBadge status={status} />
      </div>
      <div className="acard-body">
        <div className="split muted" style={{ fontSize: 'var(--fs-sm)' }}>
          <span>{date(card.date)}</span><span className="num" style={{ fontWeight: 600, color: 'var(--fg)' }}>{chf(card.amount)}</span><Confidence value={card.confidence} />
          {card.entrySeq != null && <Badge>Buchung #{card.entrySeq}</Badge>}
        </div>
        <Lines tid={tid} lines={card.lines} />
        {card.rationale && <p className="rationale">{card.rationale}</p>}
        <Findings findings={card.findings} />
        {status === 'pending' && card.policy?.reasons?.length > 0 && <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>Warum Prüfung: {card.policy.reasons.join(' · ')}</p>}
      </div>
      {status === 'pending' && (
        <div className="acard-foot">
          <button className="btn primary sm" onClick={() => act('approve')} disabled={!!busy}>{busy === 'approve' ? <Spinner /> : <I icon={Check} sm />} Freigeben</button>
          <button className="btn sm" onClick={() => navigate(`/t/${tid}/review?p=${card.proposalId}`)}><I icon={Pencil} sm /> Bearbeiten</button>
          <button className="btn ghost sm" onClick={() => act('reject')} disabled={!!busy}><I icon={X} sm /> Ablehnen</button>
        </div>
      )}
    </div>
  );
}

/**
 * A question the agent asked, in the thread. The client may answer it elsewhere (the page Rückfragen, a reply to the
 * e-mail): `open` comes from the page's list of open questions, which follows the live feed, and once it is false the
 * card fetches the question and shows the answer instead of the buttons (ADR-060). `live` refetches on a later change.
 */
export function QuestionCard({ tid, card, open, live = 0 }: { tid: string; card: any; open?: boolean; live?: number }) {
  const { toast } = useApp();
  const [answer, setAnswer] = useState<string | null>(null);
  const [elsewhere, setElsewhere] = useState<{ answer: string; channel: 'web' | 'email' | null } | null>(null);
  useEffect(() => {
    if (open !== false || answer) return;
    let alive = true;
    api.get<any>(`/api/t/${tid}/questions/${card.questionId}`)
      .then((q) => { if (alive && q.status === 'answered') setElsewhere({ answer: q.answer, channel: q.answer_channel }); })
      .catch(() => { /* a deleted sandbox question: keep the card as it is */ });
    return () => { alive = false; };
  }, [tid, card.questionId, open, live, answer]);
  const send = async (a: string) => {
    try { await api.post(`/api/t/${tid}/questions/${card.questionId}/answer`, { answer: a }); setAnswer(a); toast('Antwort gespeichert und dem Vorschlag angehängt.'); }
    catch (e: any) { toast(e.message, 'error'); }
  };
  const shown = answer ?? elsewhere?.answer ?? null;
  const via = !answer && elsewhere ? (elsewhere.channel === 'email' ? ' per E-Mail' : ' in Kontor') : '';
  return (
    <div className="acard">
      <div className="acard-head"><I icon={MessageCircleQuestion} /><span className="title" style={{ whiteSpace: 'pre-line' }}>{card.question}</span></div>
      <div className="acard-foot">
        {shown ? <span className="muted" style={{ overflowWrap: 'anywhere' }}>Antwort{via}: «{shown}»</span> : (card.options?.length ? card.options : ['Ja', 'Nein']).map((o: string) => <button key={o} className="btn sm" onClick={() => send(o)}>{o}</button>)}
      </div>
    </div>
  );
}

export function ActionCard({ tid, card }: { tid: string; card: any }) {
  const { toast, bump } = useApp();
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const run = async () => {
    if (card.action === 'open') { navigate(`/t/${tid}${card.params.href}`); return; }
    setState('busy');
    try {
      // The click on this button is the person's explicit confirmation (K-013): the server files or books only with it.
      await api.post(`/api/t/${tid}/actions/${card.action}`, { ...card.params, confirm: true });
      setState('done'); toast(`${card.label}: erledigt`); bump();
    } catch (e: any) { setState('idle'); toast(e.message, 'error'); }
  };
  return (
    <div className="acard">
      <div className="acard-head"><span className="title">{card.label}</span>{card.action !== 'open' && <Badge kind="accent">Freigabe durch Mensch</Badge>}</div>
      <div className="acard-body"><p className="rationale">{card.summary}</p></div>
      <div className="acard-foot">
        {state === 'done' ? <Badge kind="pos" dot>Ausgeführt</Badge> : (
          <button className="btn primary sm" onClick={run} disabled={state === 'busy'}>{state === 'busy' ? <Spinner /> : <I icon={card.action === 'open' ? ArrowRight : Play} sm />} {card.action === 'open' ? 'Öffnen' : 'Jetzt ausführen'}</button>
        )}
      </div>
    </div>
  );
}

export function ReportCard({ tid, card }: { tid: string; card: any }) {
  return (
    <div className="acard">
      <div className="acard-head"><span className="title">{card.title}</span>{card.href && <button className="btn ghost sm" onClick={() => navigate(`/t/${tid}${card.href}`)}>Details <I icon={ArrowRight} sm /></button>}</div>
      <table className="data compact" data-copy-surface>
        <tbody>{card.rows.map(([k, v]: [string, string], i: number) => <tr key={i}><td>{k}</td><td className="r num">{v}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

export function AgentCard({ tid, card, openQuestions, live }: { tid: string; card: any; openQuestions?: Set<string> | null; live?: number }) {
  switch (card.kind) {
    case 'proposal': return <ProposalCard tid={tid} card={card} />;
    case 'question': return <QuestionCard tid={tid} card={card} open={openQuestions ? openQuestions.has(card.questionId) : undefined} live={live} />;
    case 'action': return <ActionCard tid={tid} card={card} />;
    case 'report': return <ReportCard tid={tid} card={card} />;
    default: return null;
  }
}
